// TableContent.tsx
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { FC } from 'react';
import {
  RPMNoteAllergy,
  RPMNoteDiagnosis,
  RPMNoteInteraction,
  RPMNoteMedication,
  RPMNotePlanItem,
  RPMNoteSection,
  RPMNoteSectionKey,
  RPMNoteSubSectionKey,
} from '../../../interfaces/billing.rpm';

interface TableContentProps {
  section: RPMNoteSection;
}

interface ColumnType {
  key: string;
  label: string;
}

export const TableContent: FC<TableContentProps> = ({ section }) => {
  const renderTable = <T extends Record<string, any>>(columns: ColumnType[], content: T[]) => {
    return (
      <Table
        size='small'
        sx={{
          mb: 2,
          border: '1px solid black',
          borderCollapse: 'collapse',
          '& td, & th': {
            border: '1px solid black',
            padding: '6px',
          },
        }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
            {columns.map((column: ColumnType) => {
              return <TableCell key={String(column.key)}>{column.label}</TableCell>;
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {content.map((item, idx) => (
            <TableRow
              key={idx}
              sx={{
                backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa',
              }}>
              {columns.map((col) => (
                <TableCell key={String(col.key)} sx={{ border: '1px solid black' }}>
                  {item[col.key] ?? '-'}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderPlanSectionTable = () => {
    const planItems = Array.isArray(section.content)
      ? section.content
      : ((section.content as any)?.content?.[0]?.content as RPMNotePlanItem[]);

    if (!planItems) {
      return null;
    }

    const columns = [
      { key: 'type', label: 'Type' },
      { key: 'description', label: 'Description' },
      { key: 'cptCode', label: 'CPT Code' },
      { key: 'modifiers', label: 'Modifiers' },
      { key: 'quantity', label: 'Quantity' },
    ];

    return renderTable<RPMNotePlanItem>(columns, section.content as RPMNotePlanItem[]);
  };

  const renderAssessmentSectionTable = () => {
    if (!Array.isArray(section.content)) {
      return null;
    }

    const columns = [
      { key: 'code', label: 'Code' },
      { key: 'description', label: 'Description' },
      { key: 'type', label: 'Type' },
    ];

    return renderTable<RPMNotePlanItem>(columns, section.content as RPMNoteDiagnosis[]);
  };

  const renderInteractionLogSectionTable = () => {
    if (!Array.isArray(section.content)) {
      return null;
    }

    const columns = [
      { key: 'date', label: 'Date' },
      { key: 'type', label: 'Type' },
      { key: 'description', label: 'Description' },
    ];

    return renderTable<RPMNoteInteraction>(columns, section.content as RPMNoteInteraction[]);
  };

  const renderMedicalAllergiesSectionTable = () => {
    if (!Array.isArray(section.content)) {
      return null;
    }

    const subsections = section.content as {
      key: string;
      isTable: boolean;
      content: RPMNoteMedication[] | RPMNoteAllergy[];
    }[];

    const medSection = subsections.find((s) => s.key === RPMNoteSubSectionKey.CURRENT_MEDICATIONS);
    const allergySection = subsections.find((s) => s.key === RPMNoteSubSectionKey.ALLERGIES);

    const medicationColumns = [
      { key: 'name', label: 'Medication' },
      { key: 'sig', label: 'Sig' },
      { key: 'dispense', label: 'Dispense' },
    ];

    const allergiesColumns = [
      { key: 'allergen', label: 'Allergen' },
      { key: 'reaction', label: 'Reaction' },
    ];

    return (
      <>
        {medSection?.content && (
          <>
            <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
              Current Medications
            </Typography>
            {renderTable<RPMNoteMedication>(
              medicationColumns,
              medSection.content as RPMNoteMedication[],
            )}
          </>
        )}

        {allergySection?.content && (
          <>
            <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
              Allergies
            </Typography>
            {renderTable<RPMNoteAllergy>(
              allergiesColumns,
              allergySection.content as RPMNoteAllergy[],
            )}
          </>
        )}
      </>
    );
  };

  switch (section.key) {
    case RPMNoteSectionKey.INTERACTION_LOG:
      return renderInteractionLogSectionTable();

    case RPMNoteSectionKey.MEDICATIONS_ALLERGIES:
      return renderMedicalAllergiesSectionTable();

    case RPMNoteSectionKey.ASSESSMENT:
      return renderAssessmentSectionTable();

    case RPMNoteSectionKey.PLAN:
      return renderPlanSectionTable();

    default:
      return null;
  }
};
